import { useCallback, useEffect, useRef, useState } from "react"
import { Helmet, HelmetProvider } from "react-helmet-async"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"
import api from "../api"
import { ReactComponent as CalendarIcon } from "../assets/images/icons/ic-calendar.svg"
import { ReactComponent as MaterialIcon } from "../assets/images/icons/ic-files-inactive.svg"
import { ReactComponent as LessonIcon } from "../assets/images/icons/ic-lessons-inactive.svg"
import { ReactComponent as StudentsIcon } from "../assets/images/icons/ic-students.svg"
import { Each } from "../common/Each"
import { ButtonStatus } from "../common/constants"
import Breadcrumb from "../components/Breadcrumb"
import StatusBadge from "../components/StatusBadge"
import Button from "../components/Button"
import MeatBallsMenu from "../components/MeatBallsMenu"
import MaterialButton from "../components/MaterialButton"
import Skeleton from "../components/Skeleton"
import Table from "../components/Table"
import TextInput from "../components/TextInput"
import Card from "../components/cards/Card"
import AlertDialog from "../components/dialogs/AlertDialog"
import Dialog from "../components/dialogs/Dialog"
import HeaderFooterLayout from "../components/layouts/HeaderFooterLayout"
import Select from "../components/Select"
import { DialogStatus } from "../enums"
import { formatDateV2, formatTimeV2 } from "../utils"
import styles from "./Exercise.module.css"
import Markdown from "react-markdown"

const keepLatestSubmissionPerUser = (submissions = []) => {
  const latestByUser = new Map()

  for (const submission of submissions) {
    const userId = submission?.user?.id
    if (!userId) {
      continue
    }

    const currentLatest = latestByUser.get(userId)
    if (!currentLatest) {
      latestByUser.set(userId, submission)
      continue
    }

    const currentDate = new Date(currentLatest.created_at ?? 0)
    const nextDate = new Date(submission.created_at ?? 0)
    if (nextDate >= currentDate) {
      latestByUser.set(userId, submission)
    }
  }

  return Array.from(latestByUser.values())
}

const Exercise = () => {
  const { t } = useTranslation()
  const { exerciseId } = useParams()
  const navigate = useNavigate()

  const [exercise, setExercise] = useState(null)
  const [loading, setLoading] = useState(true)

  const [usersExercises, setUsersExercises] = useState([])
  const [filteredUsersExercises, setFilteredUsersExercises] = useState([])
  const [submissionsFilter, setSubmissionsFilter] = useState(0)
  const [studentSearchQuery, setStudentSearchQuery] = useState("")

  const [openAlert, setOpenAlert] = useState(false)
  const [alertStatus, setAlertStatus] = useState(DialogStatus.Default)
  const [closeTimeout, setCloseTimeout] = useState(null)

  const [openCorrectionDetails, setOpenCorrectionDetails] = useState(false)
  const [selectedCorrection, setSelectedCorrection] = useState(null)
  const [openSubmission, setOpenSubmission] = useState(false)
  const [selectedSubmission, setSelectedSubmission] = useState(null)

  const [openEditDialog, setOpenEditDialog] = useState(false)
  const [waitingEdit, setWaitingEdit] = useState(null)
  const [courses, setCourses] = useState([])
  const [editions, setEditions] = useState([])
  const [modules, setModules] = useState([])
  const [lessons, setLessons] = useState([])
  const [course, setCourse] = useState(null)
  const [edition, setEdition] = useState(null)
  const [module, setModule] = useState(null)
  const [lesson, setLesson] = useState(null)
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [expiresAt, setExpiresAt] = useState(null)

  const closeTimeoutRef = useRef(null)

  const getExercise = useCallback(async () => {
    if (!exerciseId) {
      return
    }

    setLoading(true)
    try {
      const detail = await api.get(`/admin/exercises/${exerciseId}`)

      if (!detail?.id) {
        setExercise(null)
        setUsersExercises([])
        setFilteredUsersExercises([])
        setLoading(false)
        return
      }

      const users = keepLatestSubmissionPerUser(detail.users_exercises ?? [])
      setExercise(detail)
      setUsersExercises(users)
      setFilteredUsersExercises(users)
    } catch (e) {
      console.error(e)
      setExercise(null)
      setUsersExercises([])
      setFilteredUsersExercises([])
    }
    setLoading(false)
  }, [exerciseId])

  const getCourses = useCallback(async () => {
    try {
      const data = await api.get("/admin/courses")
      setCourses(Array.from(
        data.reduce((map, c) => {
          if (!map.has(c.id)) {
            map.set(c.id, { ...c, label: c.name })
          }
          return map
        }, new Map()).values()
      ))
    } catch (e) {
      console.error(e)
    }
  }, [])

  const getEditions = useCallback(async () => {
    if (!course?.id) {
      setEditions([])
      return
    }

    try {
      let data = await api.get(`/admin/courses/${course.id}/editions`)
      data = data.filter((item) => item.modules && item.modules.length > 0)
      setEditions(data.map((item) => ({ ...item, label: item.name })))
    } catch (e) {
      console.error(e)
    }
  }, [course])

  useEffect(() => {
    getExercise()
  }, [getExercise])

  useEffect(() => {
    getCourses()
  }, [getCourses])

  useEffect(() => {
    if (!openEditDialog || !exercise) {
      return
    }

    setName(exercise.name ?? "")
    setDescription(exercise.description ?? "")
    setExpiresAt(exercise.expires_at ?? exercise.expiresAt ?? null)

    const currentCourse = courses.find((c) => c.id === (exercise.course?.id ?? exercise.module?.course?.id))
    if (currentCourse) {
      setCourse(currentCourse)
    }
  }, [openEditDialog, exercise, courses])

  useEffect(() => {
    if (course) {
      getEditions()
    } else {
      setEditions([])
      setModules([])
      setLessons([])
      setEdition(null)
      setModule(null)
      setLesson(null)
    }
  }, [course, getEditions])

  useEffect(() => {
    if (!edition) {
      setModules([])
      setLessons([])
      return
    }

    setModules((edition.modules ?? []).map((m) => ({ ...m, label: m.name })))
  }, [edition])

  useEffect(() => {
    if (!module) {
      setLessons([])
      return
    }

    setLessons((module.lessons ?? []).map((l) => ({ ...l, label: l.name })))
  }, [module])

  useEffect(() => {
    if (!openEditDialog || !exercise || editions.length === 0 || edition) {
      return
    }

    const currentEdition = editions.find((e) => e.id === (exercise.edition?.id ?? exercise.module?.edition?.id))
    if (currentEdition) {
      setEdition(currentEdition)
    }
  }, [openEditDialog, exercise, editions, edition])

  useEffect(() => {
    if (!openEditDialog || !exercise || modules.length === 0 || module) {
      return
    }

    const currentModule = modules.find((m) => m.id === exercise.module?.id)
    if (currentModule) {
      setModule(currentModule)
    }
  }, [openEditDialog, exercise, modules, module])

  useEffect(() => {
    if (!openEditDialog || !exercise || lessons.length === 0 || lesson || !exercise.lesson?.id) {
      return
    }

    const currentLesson = lessons.find((l) => l.id === exercise.lesson?.id)
    if (currentLesson) {
      setLesson(currentLesson)
    }
  }, [openEditDialog, exercise, lessons, lesson])

  const applySubmissionFilters = useCallback((filterIndex, searchText, sourceExercises) => {
    const source = sourceExercises ?? usersExercises
    const normalizedSearch = (searchText ?? "").toLowerCase().trim()

    let filtered = source
    if (filterIndex === 1) {
      filtered = filtered.filter((e) => e.correction || (e.corrections && e.corrections.length > 0))
    } else if (filterIndex === 2) {
      filtered = filtered.filter((e) => !e.correction && (!e.corrections || e.corrections.length === 0))
    }

    if (normalizedSearch) {
      filtered = filtered.filter((e) => {
        const fullName = `${e.user?.name ?? ""} ${e.user?.surname ?? ""}`.toLowerCase().trim()
        return fullName.includes(normalizedSearch)
      })
    }

    setFilteredUsersExercises(filtered)
  }, [usersExercises])

  useEffect(() => {
    applySubmissionFilters(submissionsFilter, studentSearchQuery, usersExercises)
  }, [usersExercises, submissionsFilter, studentSearchQuery, applySubmissionFilters])

  const onDownloadSubmission = useCallback(async (submission) => {
    if (!submission?.medias?.length) {
      return
    }

    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

    const resolveFileName = (media, index) => {
      if (media?.name && String(media.name).trim() !== "") {
        return media.name
      }

      if (media?.url && typeof media.url === "string") {
        try {
          const pathname = new URL(media.url).pathname
          const candidate = decodeURIComponent(pathname.split("/").pop() || "")
          if (candidate.trim() !== "") {
            return candidate
          }
        } catch (_) {
          // no-op
        }
      }

      return `submission-file-${index + 1}`
    }

    for (let index = 0; index < submission.medias.length; index++) {
      const media = submission.medias[index]
      if (!media?.url) {
        continue
      }

      const fileName = resolveFileName(media, index)

      try {
        const response = await fetch(media.url)
        if (!response.ok) {
          throw new Error(`Download failed with status ${response.status}`)
        }

        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)

        const link = document.createElement("a")
        link.href = blobUrl
        link.download = fileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(blobUrl)
      } catch (error) {
        console.error("Unable to download media via blob, using direct link fallback", error)

        const link = document.createElement("a")
        link.href = media.url
        link.download = fileName
        link.target = "_blank"
        link.rel = "noopener noreferrer"
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }

      await sleep(180)
    }
  }, [])

  const getSubmissionActions = useCallback((submission) => {
    const filesCount = Array.isArray(submission?.medias) ? submission.medias.length : 0
    const downloadLabel = filesCount === 1 ? "Scarica 1 file" : `Scarica ${filesCount} file`

    const actions = [
      { label: 'Vedi consegna', onClick: () => { setSelectedSubmission(submission); setOpenSubmission(true) } },
      {
        label: downloadLabel,
        onClick: () => onDownloadSubmission(submission),
        disabled: filesCount === 0,
      },
    ]

    if (submission?.correction) {
      actions.push({
        label: "Vedi correzione",
        onClick: () => {
          setSelectedSubmission(submission)
          setSelectedCorrection(submission.correction)
          setOpenCorrectionDetails(true)
        },
      })
    }

    return actions
  }, [onDownloadSubmission])

  const handleFilterChange = useCallback((index) => {
    setSubmissionsFilter(index)
    applySubmissionFilters(index, studentSearchQuery)
  }, [applySubmissionFilters, studentSearchQuery])

  const handleStudentSearch = useCallback((text) => {
    const query = text ?? ""
    setStudentSearchQuery(query)
    applySubmissionFilters(submissionsFilter, query)
  }, [applySubmissionFilters, submissionsFilter])

  const onCloseEditDialog = () => {
    setOpenEditDialog(false)
    setCourse(null)
    setEdition(null)
    setModule(null)
    setLesson(null)
    setName("")
    setDescription("")
    setExpiresAt(null)
    setWaitingEdit(null)
  }

  const editExercise = useCallback(async () => {
    if (!exercise?.id || !module?.id || !name) {
      return
    }

    setWaitingEdit(ButtonStatus.Loading)
    const payload = {
      name,
      module_id: module.id,
    }

    if (description) {
      payload.description = description
    }

    if (lesson?.id) {
      payload.lesson_id = lesson.id
    }

    if (expiresAt) {
      payload.expires_at = new Date(expiresAt).toISOString()
    }

    try {
      await api.put(`/admin/exercises/${exercise.id}`, payload)
      setWaitingEdit(ButtonStatus.Success)
      await getExercise()
      closeTimeoutRef.current = setTimeout(() => {
        onCloseEditDialog()
      }, 600)
    } catch (e) {
      console.error(e)
      setWaitingEdit(ButtonStatus.Error)
    }
  }, [exercise, module, name, description, lesson, expiresAt, getExercise])

  const deleteExercise = useCallback(async () => {
    if (!exercise?.id) {
      return
    }

    try {
      setAlertStatus(DialogStatus.Loading)
      await api.delete(`/admin/exercises/${exercise.id}`)
      setAlertStatus(DialogStatus.Success)
      setCloseTimeout(setTimeout(() => {
        setOpenAlert(false)
        setAlertStatus(DialogStatus.Default)
        navigate("/exercises")
      }, 1000))
    } catch (e) {
      console.error(e)
      setAlertStatus(DialogStatus.Error)

      setCloseTimeout(setTimeout(() => {
        setOpenAlert(false)
        setAlertStatus(DialogStatus.Default)
      }, 1000))
    }
  }, [exercise, navigate])

  useEffect(() => {
    return () => {
      if (closeTimeout) {
        clearTimeout(closeTimeout)
      }
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current)
      }
    }
  }, [closeTimeout])

  return (
    <HeaderFooterLayout>
      <HelmetProvider>
        <Helmet>
          <title>{exercise?.name ?? t("exercises.title")}</title>
        </Helmet>
      </HelmetProvider>
      <div className={styles.container}>
        <div className={styles.section}>
          <div className={styles.sectionInner}>
            <Breadcrumb items={[{ label: "Didattica" }, { label: "Esercizi", to: "/exercises" }, { label: exercise?.name || "Dettaglio" }]} />
            <div className={styles.header}>
              <h1 className={styles.title}>{exercise?.name ?? t("exercises.title")}</h1>
              <div className={styles.actions}>
                <Button
                style={{color: "var(--sf-red)"}}
                  appearance="subtle"
                  accentColor={"var(--sf-red)"}
                  onClick={() => setOpenAlert(true)}
                >
                  {t("delete")}
                </Button>
                <Button
                  appearance="subtle"
                  accentColor={"var(--primary)"}
                  onClick={() => setOpenEditDialog(true)}
                >
                  {t("edit")}
                </Button>
              </div>
            </div>
            <Card appearance="modern" title="Dettagli esercizio">
              {
                loading === true &&
                <>
                  <div className={styles.cardContent}>
                    <div className={styles.bottomCard}>
                      <div className={`${styles.labeled} ${styles.descriptionField}`}>
                        <Skeleton type="rect" width="96px" height="16px" borderRadius="8px" />
                        <Skeleton type="rect" width="70%" height="16px" borderRadius="8px" />
                        <Skeleton type="rect" width="48%" height="16px" borderRadius="8px" />
                      </div>
                      <div className={styles.labeled}>
                        <Skeleton type="rect" width="96px" height="16px" borderRadius="8px" />
                        <Skeleton type="rect" width="196px" height="16px" borderRadius="8px" />
                      </div>
                      <div className={styles.labeled}>
                        <Skeleton type="rect" width="196px" height="16px" borderRadius="8px" />
                        <Skeleton type="rect" width="96px" height="16px" borderRadius="8px" />
                      </div>
                    </div>
                  </div>
                </>
              }

              {
                loading === false && exercise &&
                <>
                  <div className={styles.cardContent}>
                    <div className={styles.bottomCard}>
                      <div className={`${styles.labeled} ${styles.descriptionField}`}>
                        <div className={styles.label}>Descrizione</div>
                        <div className={`${styles.value} ${styles.descriptionValue}`}>
                          {
                            exercise?.description
                              ? <Markdown>{exercise.description}</Markdown>
                              : <span className={styles.emptyDescription}>Nessuna descrizione disponibile.</span>
                          }
                        </div>
                      </div>
                      {
                        exercise?.expires_at &&
                        <div className={styles.labeled}>
                          <div className={styles.label}>{t("exercises.expiresAt")}</div>
                          <div className={styles.value}><CalendarIcon /> {formatDateV2(exercise.expires_at)} {formatTimeV2(exercise.expires_at)}</div>
                        </div>
                      }
                      <div className={styles.labeled}>
                        <div className={styles.label}>{t("exercises.submitNumber")}</div>
                        <div className={styles.value}><StudentsIcon />{usersExercises.length} {t("outOf")} {exercise?.total_users ?? exercise?.users ?? 0}</div>
                      </div>
                      <div className={styles.labeled}>
                        <div className={styles.label}>{t("exercises.lesson.label")}</div>
                        <div className={styles.value}><LessonIcon />{exercise?.lesson?.name ?? t("exercises.lesson.empty")}</div>
                      </div>
                      <div className={styles.labeled}>
                        <div className={styles.label}>{t("exercises.uploadedMaterials")}</div>
                        <div className={styles.value}><MaterialIcon />{exercise?.medias?.length ?? 0}</div>
                      </div>
                    </div>
                    {
                      exercise?.medias && exercise.medias.length > 0 &&
                      <div className={styles.labeled}>
                        <div className={styles.label}>{t("exercises.materials")}</div>
                        <div className={styles.studentFilesContainer}>
                          <div className={styles.studentFiles}>
                            <Each
                              of={exercise.medias}
                              render={(file) => (
                                <div className={styles.studentFile}>
                                  <MaterialButton modern thumbnail material={file} downloadable={true} />
                                </div>
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    }
                  </div>
                </>
              }
            </Card>

            <Card appearance="modern" integratedTable title="Consegne degli studenti" actions={<span className={styles.resultCount}>{loading ? 'Caricamento…' : `${filteredUsersExercises.length} consegne`}</span>}>
              <div className={styles.submissionToolbar}>
                <div className={styles.submissionFilter}>
                  <Select appearance="modern" portal
                    selected={[
                      { id: 0, name: "Tutti" },
                      { id: 1, name: t("exercises.correctExercises") },
                      { id: 2, name: t("exercises.toBeCorrectedExercises") },
                    ].find((option) => option.id === submissionsFilter)}
                    options={[
                      { id: 0, name: "Tutti" },
                      { id: 1, name: t("exercises.correctExercises") },
                      { id: 2, name: t("exercises.toBeCorrectedExercises") },
                    ]}
                    placeholder={"Tutti"}
                    onSelect={(item) => {
                      handleFilterChange(Number(item?.id ?? 0))
                    }}
                  />
                </div>
                <div className={styles.submissionSearch}>
                  <TextInput appearance="modern"
                    onKeyUp={handleStudentSearch}
                    type="search"
                    placeholder="Cerca Studente"
                    style={{ backgroundColor: "var(--background-secondary-color)" }}
                  />
                </div>
              </div>

              {
                loading === true &&
                <Table
                    integrated
                  loading
                  appearance="minimal"
                  columns={[
                    { key: "user_name", label: "STUDENTE", type: "string" },
                    { key: "created_at", label: "CONSEGNATO IL", type: "datetime" },
                    { key: "materials_count", label: "FILE", type: "number" },
                    { key: "_status", label: "STATO", type: "custom" },
                    { key: "_actions", label: "", type: "custom", handleClick: true, style: { width: 52 } },
                  ]}
                  elements={[]}
                />
              }

              {
                !loading &&
                <Table
                    integrated
                  onRowClick={row => { setSelectedSubmission(row); setOpenSubmission(true) }}
                  appearance="minimal"
                  columns={[
                    { key: "user_name", label: "STUDENTE", type: "string" },
                    { key: "created_at", label: "CONSEGNATO IL", type: "datetime" },
                    { key: "materials_count", label: "FILE", type: "number" },
                    { key: "_status", label: "STATO", type: "custom" },
                    { key: "_actions", label: "", type: "custom", handleClick: true, style: { width: 52 } },
                  ]}
                  elements={filteredUsersExercises.map((ue) => ({
                    ...ue,
                    user_name: `${ue.user?.name ?? ""} ${ue.user?.surname ?? ""}`.trim(),
                    materials_count: ue.medias?.length ?? 0,
                    _status: {
                      value: ue.correction ? "Corretta" : "Da correggere",
                      type: "string",
                      component: (
                        <StatusBadge appearance="soft" label={ue.correction ? "Corretta" : "Da correggere"} color={ue.correction ? "var(--primary)" : "var(--sf-orange)"} />
                      )
                    },
                    _actions: {
                      value: ue.user?.id,
                      type: "number",
                      component: <MeatBallsMenu actions={getSubmissionActions(ue)} />
                    }
                  }))}
                />
              }
              {!loading && filteredUsersExercises.length === 0 && <div className={styles.empty}>Nessuna consegna corrisponde ai filtri selezionati.</div>}
            </Card>
          </div>
        </div>

        <Dialog appearance="modern"
          open={openEditDialog}
          title={"Modifica esercizio"}
          onClose={onCloseEditDialog}
          contentMaxHeight="78vh"
          style={{ width: "720px", maxWidth: "calc(100vw - 32px)" }}
          action={
            <Button onClick={editExercise} status={waitingEdit} disabled={!course || !edition || !module || !name}>
              Salva modifiche
            </Button>
          }
        >
          <div className={styles.dialogForm}>
            <TextInput appearance="modern"
              type="text" label="Nome esercizio"
              placeholder={t("exercises.name")}
              onKeyUp={(value) => setName(value)}
              value={name}
            />

            <TextInput appearance="modern"
              type="textarea" label="Descrizione"
              placeholder={t("exercises.description")}
              value={description}
              onKeyUp={(value) => setDescription(value)}
              style={{ minHeight: "96px" }}
            />

            <div className={styles.formField}><span>Corso</span><Select appearance="modern" portal
              disabled={courses.length === 0}
              placeholder={"Corso"}
              selected={courses.find((c) => c.id === course?.id) ?? null}
              options={[...courses].sort((a, b) => a.name.localeCompare(b.name))}
              onSelect={(item) => {
                if (item?.id) {
                  setCourse(item)
                  setEdition(null)
                  setModule(null)
                  setLesson(null)
                }
              }}
            /></div>

            <div className={styles.formField}><span>Edizione</span><Select appearance="modern" portal
              disabled={editions.length === 0}
              placeholder={"Edizione"}
              selected={editions.find((e) => e.id === edition?.id) ?? null}
              options={[...editions].sort((a, b) => a.name.localeCompare(b.name))}
              onSelect={(item) => {
                if (item?.id) {
                  setEdition(item)
                  setModule(null)
                  setLesson(null)
                }
              }}
            /></div>

            <div className={styles.formField}><span>Modulo</span><Select appearance="modern" portal
              disabled={modules.length === 0}
              placeholder={"Modulo"}
              selected={modules.find((m) => m.id === module?.id) ?? null}
              options={[...modules].sort((a, b) => a.name.localeCompare(b.name))}
              onSelect={(item) => {
                if (item?.id) {
                  setModule(item)
                  setLesson(null)
                }
              }}
            /></div>

            <div className={styles.formField}><span>Lezione (facoltativa)</span><Select appearance="modern" portal
              disabled={lessons.length === 0}
              placeholder={"Lezione"}
              selected={lessons.find((l) => l.id === lesson?.id) ?? null}
              options={lessons}
              onSelect={(item) => {
                if (item?.id) {
                  setLesson(item)
                }
              }}
            /></div>

            <TextInput appearance="modern"
              type="date" label="Scadenza (facoltativa)"
              value={expiresAt ? new Date(`${expiresAt}`.replace("Z", "") + "Z") : null}
              placeholder={t("exercises.expiresAt")}
              onKeyUp={(date) => {
                if (date) {
                  setExpiresAt(date.toISOString().split(".")[0])
                } else {
                  setExpiresAt(null)
                }
              }}
            />
          </div>
        </Dialog>

        <Dialog appearance="modern" open={openSubmission} title="Dettaglio consegna" style={{ width: 800, maxWidth: 'calc(100vw - 32px)' }} contentMaxHeight="78vh" onClose={() => setOpenSubmission(false)}>
          <div className={styles.submissionDetail}>
            <div className={styles.submissionIdentity}><div><h2>{selectedSubmission?.user?.name} {selectedSubmission?.user?.surname}</h2><p>Consegnato il {selectedSubmission?.created_at ? `${formatDateV2(selectedSubmission.created_at)} · ${formatTimeV2(selectedSubmission.created_at)}` : '—'}</p></div><StatusBadge appearance="soft" label={selectedSubmission?.correction ? 'Corretta' : 'Da correggere'} color={selectedSubmission?.correction ? 'var(--primary)' : 'var(--sf-orange)'} /></div>
            <section className={styles.submissionBlock}><div className={styles.submissionBlockHeader}><h3>File consegnati</h3><Button appearance="subtle" disabled={!selectedSubmission?.medias?.length} onClick={() => onDownloadSubmission(selectedSubmission)}>Scarica tutti</Button></div>
              <div className={styles.submissionFiles}>{(selectedSubmission?.medias || []).map((file, index) => <MaterialButton modern thumbnail key={file.id || index} material={file} downloadable />)}</div>
              {!selectedSubmission?.medias?.length && <p>Nessun file allegato.</p>}
            </section>
            <section className={styles.submissionBlock}><h3>Correzione</h3>{selectedSubmission?.correction ? <><div className={styles.correctionReadOnlyMessage}><Markdown>{selectedSubmission.correction.message || 'Nessun commento.'}</Markdown></div><div className={styles.submissionFiles}>{(selectedSubmission.correction.medias || []).map((file, index) => <MaterialButton modern thumbnail key={file.id || index} material={file} downloadable />)}</div>{selectedSubmission.correction.created_at && <p>{formatDateV2(selectedSubmission.correction.created_at)} · {formatTimeV2(selectedSubmission.correction.created_at)}</p>}</> : <p>Questa consegna non è ancora stata corretta.</p>}</section>
          </div>
        </Dialog>

        <Dialog appearance="modern"
          open={openCorrectionDetails}
          title={`Correzione per ${selectedSubmission?.user?.name ?? ""} ${selectedSubmission?.user?.surname ?? ""}`.trim()}
          onClose={() => {
            setOpenCorrectionDetails(false)
            setSelectedCorrection(null)
          }}
        >
          <div className={`${styles.correctionDialogReadOnly} ${styles.fullWidthDialogContent}`}>
            <div className={styles.correctionReadOnlyMessage}><Markdown>{selectedCorrection?.message ?? ""}</Markdown></div>
            <div className={styles.correctionReadOnlyMaterials}>
              <Each
                of={selectedCorrection?.medias ?? []}
                render={(media) => (
                  <div className={styles.correctionReadOnlyFile}>
                    <MaterialButton modern thumbnail material={media} downloadable />
                  </div>
                )}
              />
            </div>
            {selectedCorrection?.created_at &&
              <div className={styles.correctionReadOnlyDate}>
                {formatDateV2(selectedCorrection.created_at, { day: "numeric", month: "long", year: "numeric" })} - {formatTimeV2(selectedCorrection.created_at)}
              </div>
            }
          </div>
        </Dialog>

        <AlertDialog dialogProps={{ appearance: "modern" }}
          open={openAlert}
          title={t("exercises.deleteTitle")}
          text={t("exercises.deleteText")}
          status={alertStatus}
          onClose={() => {
            setOpenAlert(false)
            setAlertStatus(DialogStatus.Default)
            if (closeTimeout) {
              clearTimeout(closeTimeout)
            }
          }}
          actions={[
            {
              label: t("exercises.deleteConfirm").toUpperCase(),
              onClick: deleteExercise,
            }
          ]}
        />
      </div>
    </HeaderFooterLayout>
  )
}

export default Exercise
